import * as React from 'react';
import { XYCoord, useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { Button, FormGroup, Input, Label } from '../../../../components/Bootstrap';
import { MoveIcon } from './MoveIcon';
import { RemoveIcon } from './RemoveIcon';
import { SalesCategory, MenuCategory } from '../types';
import { getIdForCategory } from './utils';
import { isNewMenuCategory, canSubmitCategory } from '../utils';

interface CategoryRowProps {
  hasTriedSubmitting: boolean;
  category: MenuCategory;
  saleCategories: SalesCategory[];
  onChange(category: MenuCategory);
  onRemove(category: MenuCategory);

  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const CategoryRow: React.FC<CategoryRowProps> = (props: CategoryRowProps) => {
  const { onChange, hasTriedSubmitting, saleCategories, onRemove, category, index, moveCard } = props;

  const previewRef = React.useRef<HTMLDivElement>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const [{}, drop] = useDrop({
    accept: 'category',
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current!.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{}, drag, preview] = useDrag({
    item: { type: 'category', id: getIdForCategory(category), index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));
  preview(previewRef);

  const onChangeName = React.useCallback(
    (e) => {
      const { value } = e.target;
      onChange({
        ...category,
        name: value,
      });
    },
    [onChange, category]
  );

  const onChangeSalesCategory = React.useCallback(
    (e) => {
      const { value } = e.target;
      onChange({
        ...category,
        salesCategoryId: value,
      });
    },
    [onChange, category]
  );

  const [isDeleting, setIsDeleting] = React.useState(false);

  const onClickConfirmDelete = React.useCallback(() => {
    onRemove(category);
  }, [onRemove, category]);

  const onClickRemove = React.useCallback(() => {
    if (!isNewMenuCategory(category) && category.items.length > 0) {
      setIsDeleting(true);
    } else {
      onClickConfirmDelete();
    }
  }, [setIsDeleting, onClickConfirmDelete, category]);

  const onClickCancelDelete = React.useCallback(() => {
    setIsDeleting(false);
  }, [setIsDeleting]);

  const isSelectedSaleCategory = React.useCallback(
    (saleCategory: SalesCategory): boolean => {
      if (isNewMenuCategory(category)) {
        return category.salesCategoryId === saleCategory.id;
      }

      return category.salesCategory.id === saleCategory.id;
    },
    [category]
  );

  const showError = !canSubmitCategory(category) && hasTriedSubmitting;

  return (
    <div ref={previewRef} className='edit-menu-category-row'>
      <div className='d-flex flex-row align-items-center justify-content-between mb-3'>
        <div className='edit-menu-category-input'>
          <FormGroup>
            <Label>Menu category</Label>
            <Input onChange={onChangeName} value={category.name} />
          </FormGroup>
        </div>
        <div className='edit-menu-category-input-sales-category'>
          <FormGroup className='noble-custom-select'>
            <Label>Sales category</Label>
            <Input disabled={!isNewMenuCategory(category)} onChange={onChangeSalesCategory} type='select'>
              <option selected={isNewMenuCategory(category) ? category.salesCategoryId === null : false}>
                Select...
              </option>
              {saleCategories.map((saleCategory) => (
                <option selected={isSelectedSaleCategory(saleCategory)} key={saleCategory.id} value={saleCategory.id}>
                  {saleCategory.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </div>
        <div className='edit-menu-category-row-icon' ref={ref}>
          <MoveIcon />
        </div>
        <div className='edit-menu-category-row-icon' onClick={onClickRemove}>
          <RemoveIcon />
        </div>
      </div>
      {showError && (
        <p className='text-danger'>
          {isNewMenuCategory(category)
            ? `Please enter a valid name and select a sales category`
            : `Please enter a valid name`}
        </p>
      )}
      {isDeleting && !isNewMenuCategory(category) && (
        <div>
          <p className='edit-menu-category-delete-cat'>
            Deleting this menu category will remove {category.items.length} menu items
          </p>
          <div>
            <Button onClick={onClickConfirmDelete} color='primary'>
              Confirm
            </Button>{' '}
            <Button type='button' className='me-4' color='link' onClick={onClickCancelDelete}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
